import { useEffect, useState } from 'react';
import { ChevronDown, ChevronLeft, ChevronRight, Loader2, MapPin, Star } from 'lucide-react';
import { motion, useReducedMotion } from 'framer-motion';
import { fadeInUp, warmEase } from '@/shared/motion/motionPresets';
import { avatarColorClass, getInitials } from './avatar';
import { DeliveryModeChips } from './DeliveryModeChips';
import type { NavigatorCategory, NavigatorProvider } from './navigator-results.types';

type NavigatorAdditionalProvidersProps = {
  categories: NavigatorCategory[];
  /** Surface-specific actions (Refer / Message …) for the featured provider. */
  renderProviderActions?: (
    provider: NavigatorProvider,
    category: NavigatorCategory,
  ) => React.ReactNode;
  /**
   * Matchmaking is still running, so an empty specialty is "not searched yet" —
   * it shows a spinner rather than the concierge hand-off.
   */
  isMatching?: boolean;
  /** Hands an unmatched specialty (search finished empty) to the Care Concierge. */
  onConciergeRequest?: (category: NavigatorCategory) => void;
  /** Label for the in-card Care Concierge button. */
  conciergeCtaLabel?: string;
};

const ROTATE_MS = 5000;

/**
 * Floor for the card body so every specialty occupies the same height. Without
 * it a one-line description and a four-line one give the card different sizes
 * and the page jumps under the reader on every turn.
 */
const CARD_BODY_MIN_H = 'min-h-[132px]';

/** Selection wins the featured slot, same rule the tab strip uses. */
const topMatchFor = (category: NavigatorCategory) =>
  category.providers.find((provider) => provider.isSelected) ?? category.providers[0];

const blurbFor = (category: NavigatorCategory) =>
  category.whyYouNeedThem?.trim() || category.whatTheyDo?.trim() || null;

/** Provider rendered inline — deliberately not a card inside a card. */
const ProviderRow = ({
  provider,
  compact = false,
  actions,
}: {
  provider: NavigatorProvider;
  compact?: boolean;
  actions?: React.ReactNode;
}) => {
  const [bioExpanded, setBioExpanded] = useState(false);
  const bio = provider.bio?.trim();
  // Only earns a toggle when there is something left to reveal — roughly two
  // lines at this measure.
  // ponytail: a character count, not measured overflow. Upgrade path is a ref
  // plus scrollHeight comparison if bio lengths start clustering near the line.
  const bioNeedsToggle = Boolean(bio && bio.length > 140);

  return (
  <div className="flex items-start gap-3">
    <span
      className={`flex ${compact ? 'h-8 w-8' : 'h-10 w-10'} shrink-0 items-center justify-center rounded-full text-[11px] font-semibold text-white ${avatarColorClass(provider.name)}`}
      aria-hidden
    >
      {getInitials(provider.name)}
    </span>
    <div className="min-w-0 flex-1">
      {/* The name is where the hierarchy lives. Shrinking the avatar and the
          buttons left the two rows reading as equal offers, because the most
          prominent element in each was identical: the display face at the same
          size and weight. The featured provider keeps it; the alternates step
          down to the body face, so the eye lands on the recommendation first. */}
      <p
        className={
          compact
            ? 'text-sm text-text-primary'
            : 'font-termina text-sm font-medium text-text-primary'
        }
      >
        {provider.name}
      </p>
      {provider.specialtyName && (
        <p className="text-xs text-text-secondary">{provider.specialtyName}</p>
      )}
      <p className="mt-0.5 flex flex-wrap items-center gap-x-3 gap-y-0.5 text-xs text-text-secondary">
        {provider.location && (
          <span className="inline-flex items-center gap-1">
            <MapPin className="h-3 w-3" aria-hidden />
            {provider.location}
          </span>
        )}
        {typeof provider.rating === 'number' && provider.rating > 0 && (
          <span className="inline-flex items-center gap-1">
            <Star className="h-3 w-3 fill-sage text-sage" aria-hidden />
            <span className="text-text-primary">{provider.rating.toFixed(1)}</span>
          </span>
        )}
        <DeliveryModeChips modes={provider.specialtyDeliveryModes} compact />
      </p>
      {!compact && bio && (
        <>
          {/* Clamped to two lines like the featured provider cards — an
              unbounded bio pushes the actions below the fold and they read as
              missing. The toggle is what keeps the clamp from also hiding the
              rest of what the provider had to say. */}
          <p
            className={`mt-2 text-sm leading-relaxed text-text-secondary ${
              bioExpanded ? '' : 'line-clamp-2'
            }`}
          >
            {bio}
          </p>
          {bioNeedsToggle && (
            <button
              type="button"
              onClick={() => setBioExpanded((current) => !current)}
              aria-expanded={bioExpanded}
              className="mt-1 text-xs font-medium text-sage transition-colors duration-150 ease-out motion-reduce:transition-none hover:text-[hsl(169_95%_18%)] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-border-emphasis"
            >
              {bioExpanded ? 'Less' : 'More'}
            </button>
          )}
        </>
      )}
      {actions && (
        // Alternates carry the same actions as the featured provider — a name
        // nobody can act on is a dead end — kept as the surface's own buttons
        // so the affordance reads the same, just smaller. Scaled here rather
        // than asking the surface for a second variant, so both rows in this
        // carousel stay one contract.
        //
        // Anchors as well as buttons: a surface can render an action through
        // `Button asChild` wrapping a link — View profile does — which is not
        // a <button> at all. Targeting only buttons left that one full-size
        // beside two shrunken siblings.
        <div
          className={`mt-2 flex flex-wrap items-center gap-2 ${
            compact
              ? '[&_button]:h-8 [&_button]:px-3 [&_button]:text-xs [&_a]:h-8 [&_a]:px-3 [&_a]:text-xs'
              : ''
          }`}
        >
          {actions}
        </div>
      )}
    </div>
  </div>
  );
};

/**
 * The specialties a care stage calls for beyond the four that hold the tab
 * strip, as a carousel: one in focus, the rest listed as chips beside the
 * heading so the reader can see what exists without stepping through them.
 *
 * Behaviour settled with design:
 *  - rotation pauses on hover and stops outright once options are open
 *  - opening survives moving to the next specialty, so comparing does not
 *    cost two clicks per card
 *  - the description is always visible, before and after providers land —
 *    nobody reads a dozen of them during the wait, so hiding it afterwards
 *    means it is never read
 *  - a specialty whose search has not finished shows a spinner and offers
 *    nothing to open
 */
export const NavigatorAdditionalProviders = ({
  categories,
  renderProviderActions,
  isMatching = false,
  onConciergeRequest,
  conciergeCtaLabel = 'Contact Care Concierge',
}: NavigatorAdditionalProvidersProps) => {
  const [index, setIndex] = useState(0);
  const [hovered, setHovered] = useState(false);
  const [expanded, setExpanded] = useState(false);
  // MotionConfig covers framer-motion only — a JS interval keeps running, so
  // the rotation has to opt out of movement itself.
  const prefersReducedMotion = useReducedMotion();

  const count = categories.length;

  // Specialties land one at a time while matchmaking runs. Clamp instead of
  // resetting, so an arriving specialty does not throw the reader back to the
  // first card.
  useEffect(() => {
    setIndex((current) => Math.min(current, Math.max(count - 1, 0)));
  }, [count]);

  const rotating = !hovered && !expanded && !prefersReducedMotion && count > 1;

  useEffect(() => {
    if (!rotating) return;
    const timer = setInterval(() => setIndex((current) => (current + 1) % count), ROTATE_MS);
    return () => clearInterval(timer);
  }, [rotating, count]);

  const category = categories[index];
  if (!category) return null;

  const topMatch = topMatchFor(category);
  const alternates = category.providers.filter((provider) => provider !== topMatch);
  const blurb = blurbFor(category);
  const total = category.providerCount ?? category.providers.length;
  // `expanded` is intentionally preserved here: stepping to the next specialty
  // keeps the options open.
  const step = (delta: number) => setIndex((current) => (current + delta + count) % count);

  return (
    <div
      role="group"
      aria-label="Additional specialties"
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      /* Keyboard users get the same hold a pointer gets, so tabbing through
         the chips does not race the rotation. */
      onFocusCapture={() => setHovered(true)}
      onBlurCapture={() => setHovered(false)}
    >
      <h3 className="mt-6 font-termina text-lg font-medium text-text-primary">
        Additional Providers{' '}
        <span className="text-sm font-normal text-text-secondary">
          {count} other {count === 1 ? 'specialty' : 'specialties'} matched to this care stage
        </span>
      </h3>

      {/* Own row rather than beside the heading: specialty names run long, so
          sharing the line pushed them into a ragged block with a gap under the
          title. Wrapped rather than scrolled — a sideways scroller hides most
          of the list, which is the problem this row exists to solve. */}
      <div className="-mx-1 mt-3 flex flex-wrap gap-1.5 px-1">
        {categories.map((entry, entryIndex) => (
          <button
            key={entry.id}
            type="button"
            onClick={() => setIndex(entryIndex)}
            aria-current={entryIndex === index}
            className={`shrink-0 rounded-full border px-3 py-1.5 text-xs transition-colors duration-150 ease-out motion-reduce:transition-none focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-border-emphasis ${
              entryIndex === index
                ? 'border-sage bg-sage text-white'
                : 'border-border-subtle bg-card-base text-text-secondary hover:border-sage'
            }`}
          >
            {entry.name}
          </button>
        ))}
      </div>

      <div className="mb-2 mt-3 flex items-center gap-2">
        <button
          type="button"
          onClick={() => step(-1)}
          aria-label="Previous specialty"
          className="rounded-full border border-border-subtle p-1.5 text-text-secondary transition-colors duration-150 ease-out motion-reduce:transition-none hover:border-sage focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-border-emphasis"
        >
          <ChevronLeft className="h-4 w-4" aria-hidden />
        </button>
        <button
          type="button"
          onClick={() => step(1)}
          aria-label="Next specialty"
          className="rounded-full border border-border-subtle p-1.5 text-text-secondary transition-colors duration-150 ease-out motion-reduce:transition-none hover:border-sage focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-border-emphasis"
        >
          <ChevronRight className="h-4 w-4" aria-hidden />
        </button>
        <span className="text-xs text-text-secondary">
          {index + 1} of {count}
        </span>
      </div>

      <motion.div
        key={category.id}
        variants={fadeInUp}
        initial="initial"
        animate="animate"
        transition={{ duration: 0.3, ease: warmEase }}
        // Same treatment as the featured provider card above, so the specialty
        // in focus reads as the thing to look at without shouting.
        className="relative overflow-hidden rounded-2xl border border-sage/30 bg-card-base p-5 ring-1 ring-sage/25"
      >
        <div
          className="pointer-events-none absolute inset-x-0 top-0 h-24 bg-gradient-to-b from-sage-bg to-transparent"
          aria-hidden
        />
        <div className={`relative flex flex-col ${CARD_BODY_MIN_H}`}>
          <h4 className="font-termina text-base font-medium text-text-primary">{category.name}</h4>
          {blurb && (
            <p className="mt-1.5 line-clamp-3 text-sm leading-relaxed text-text-secondary">
              {blurb}
            </p>
          )}

          <div className="mt-auto pt-3">
            {topMatch ? (
              <button
                type="button"
                onClick={() => setExpanded((open) => !open)}
                aria-expanded={expanded}
                className="inline-flex items-center gap-1.5 text-xs font-medium text-sage focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-border-emphasis"
              >
                {expanded ? 'Hide options' : `See ${total} option${total === 1 ? '' : 's'}`}
                <ChevronDown
                  className={`h-3 w-3 transition-transform duration-150 motion-reduce:transition-none ${expanded ? 'rotate-180' : ''}`}
                  aria-hidden
                />
              </button>
            ) : isMatching ? (
              <span className="inline-flex items-center gap-1.5 text-xs text-text-secondary">
                <Loader2 className="h-3 w-3 shrink-0 animate-spin" aria-hidden />
                Finding providers…
              </span>
            ) : (
              // Search finished with no match — hand this specialty to the Care
              // Concierge inline, rather than in a separate dead-end block below.
              <div>
                <p className="text-xs text-text-secondary">
                  We couldn&apos;t match a provider for this specialty.
                </p>
                {onConciergeRequest && (
                  <button
                    type="button"
                    onClick={() => onConciergeRequest(category)}
                    className="mt-2 inline-flex items-center rounded-full bg-sage px-4 py-2 text-sm font-medium text-white transition-colors duration-150 ease-out motion-reduce:transition-none hover:bg-sage/90 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-border-emphasis"
                  >
                    {conciergeCtaLabel}
                  </button>
                )}
              </div>
            )}
          </div>
        </div>

        {expanded && topMatch && (
          <motion.div
            variants={fadeInUp}
            initial="initial"
            animate="animate"
            transition={{ duration: 0.24, ease: warmEase }}
            className="relative mt-4 border-t border-border-subtle pt-4"
          >
            {/*
              Keyed by provider, not by specialty: the featured provider can
              change while the specialty does not — a later search result lands,
              or the reader swaps the selection. Without the key the row keeps
              its bio expansion, so the new provider's bio renders unclamped and
              pushes its actions out of reach.
            */}
            <ProviderRow
              key={topMatch.id}
              provider={topMatch}
              actions={renderProviderActions?.(topMatch, category)}
            />
            {alternates.length > 0 && (
              <div className="mt-4 divide-y divide-border-subtle border-t border-border-subtle">
                {alternates.map((provider) => (
                  <div key={provider.id} className="py-3">
                    <ProviderRow
                      key={provider.id}
                      provider={provider}
                      compact
                      actions={renderProviderActions?.(provider, category)}
                    />
                  </div>
                ))}
              </div>
            )}
          </motion.div>
        )}
      </motion.div>
    </div>
  );
};
